import {
  parsePhoneNumber,
  getCountryCodeForRegionCode
} from 'awesome-phonenumber'

/**
 * Check if a string is a valid phone number.
 *
 * Uses `awesome-phonenumber` under the hood. Accepts E.164 or
 * international format (e.g. "+47 906 53 27", "+4790653275").
 *
 * Usage in a zod schema:
 * ```ts
 * import { isValidPhoneNumber } from '@gat-ship/vue-components'
 *
 * const schema = z.object({
 *   phone: z.string()
 *     .refine(isValidPhoneNumber, 'Invalid phone number')
 * })
 * ```
 */
export function isValidPhoneNumber(value: string | null | undefined): boolean {
  if (!value || value.trim() === '') return false
  return parsePhoneNumber(value).valid
}

/**
 * Look up the international dial code (as a `+NN` string) for an ISO
 * 3166-1 alpha-2 country code. Returns `undefined` if the country code
 * isn't recognised.
 *
 * Useful for pre-filling a phone input when a country picker changes:
 * ```ts
 * const dial = getDialCodeForCountryCode('NO')  // '+47'
 * ```
 */
export function getDialCodeForCountryCode(
  countryCode: string | null | undefined
): string | undefined {
  if (!countryCode) return undefined
  const dial = getCountryCodeForRegionCode(countryCode.toUpperCase())
  if (!dial) return undefined
  return `+${dial}`
}
